import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Zap, 
  AlertTriangle,
  DollarSign,
  Percent,
  Clock,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  Building2,
  Hammer,
  Wallet,
  LogOut,
  CheckCircle,
  Lightbulb,
  Sparkles
} from "lucide-react";

export default function CapitalModelPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const { mainClass } = useSidebar();

  // Model Inputs
  const [acquisition, setAcquisition] = useState({
    purchasePrice: 150000,
    arv: 250000,
  });

  const [rehab, setRehab] = useState({
    estimate: 35000,
    contingency: 10,
  });

  const [capitalStack, setCapitalStack] = useState({
    downPayment: 20,
    interestRate: 10,
    loanTermMonths: 12,
    closingCosts: 5000,
  });

  const [exitStrategy, setExitStrategy] = useState({
    type: 'flip',
    holdingMonths: 6,
    sellingCosts: 8, // percentage
    monthlyHoldingCosts: 1500,
  });

  // AI Sensitivity Analysis
  const [showSensitivity, setShowSensitivity] = useState(false);

  // Calculate all outputs
  const outputs = useMemo(() => {
    const { purchasePrice, arv } = acquisition;
    const totalRehab = rehab.estimate * (1 + rehab.contingency / 100);
    const { downPayment, interestRate, loanTermMonths, closingCosts } = capitalStack;
    const { holdingMonths, sellingCosts, monthlyHoldingCosts } = exitStrategy;

    // Capital calculations
    const loanAmount = purchasePrice * (1 - downPayment / 100);
    const cashRequired = purchasePrice * (downPayment / 100) + totalRehab + closingCosts;
    const totalInvestment = purchasePrice + totalRehab + closingCosts;
    
    // Holding costs
    const monthlyInterest = (loanAmount * (interestRate / 100)) / 12;
    const totalHoldingCosts = (monthlyInterest + monthlyHoldingCosts) * holdingMonths;
    
    // Exit calculations
    const grossProfit = arv - purchasePrice - totalRehab;
    const sellingCostsAmount = arv * (sellingCosts / 100);
    const netProfit = grossProfit - totalHoldingCosts - sellingCostsAmount - closingCosts;
    const projectedSpread = netProfit;
    
    // Metrics
    const roi = cashRequired > 0 ? (netProfit / cashRequired) * 100 : 0;
    const annualizedRoi = holdingMonths > 0 ? (roi / holdingMonths) * 12 : 0;
    
    // Risk calculations
    const arvRatio = purchasePrice / arv;
    const rehabRatio = totalRehab / purchasePrice;
    let riskScore = 50;
    if (arvRatio < 0.65) riskScore -= 15;
    else if (arvRatio > 0.75) riskScore += 15;
    if (rehabRatio > 0.3) riskScore += 10;
    if (netProfit > 30000) riskScore -= 10;
    else if (netProfit < 10000) riskScore += 20;
    riskScore = Math.max(0, Math.min(100, riskScore));

    // Execution fit
    const executionFit = netProfit > 20000 && riskScore < 50 ? 'High' : 
                         netProfit > 10000 && riskScore < 70 ? 'Medium' : 'Low';

    // Exit confidence
    const exitConfidence = arvRatio < 0.7 && netProfit > 25000 ? 92 :
                           arvRatio < 0.75 && netProfit > 15000 ? 78 :
                           arvRatio < 0.8 && netProfit > 5000 ? 62 : 45;

    // Time to close estimate
    const timeToClose = holdingMonths <= 4 ? '3-4 months' :
                        holdingMonths <= 6 ? '5-6 months' :
                        holdingMonths <= 9 ? '7-9 months' : '10+ months';

    // Sensitivity: ARV drops 5%
    const arv95 = arv * 0.95;
    const netProfit95 = arv95 - purchasePrice - totalRehab - totalHoldingCosts - (arv95 * sellingCosts / 100) - closingCosts;
    const spreadDelta = netProfit95 - netProfit;

    // Capital efficiency
    const capitalEfficiency = cashRequired > 0 ? netProfit / cashRequired : 0;

    return {
      projectedSpread: netProfit,
      riskScore,
      executionFit,
      exitConfidence,
      timeToClose,
      cashRequired,
      totalInvestment,
      loanAmount,
      totalHoldingCosts,
      sellingCostsAmount,
      roi,
      annualizedRoi,
      // Sensitivity
      spreadDelta,
      netProfit95,
      capitalEfficiency,
      // Pass through for recommendations
      arvRatio,
      rehabRatio,
      grossProfit,
      totalRehab,
    };
  }, [acquisition, rehab, capitalStack, exitStrategy]);

  // AI-Powered Recommendations - Real-time suggestions based on inputs
  const recommendations = useMemo(() => {
    const suggestions = [];
    const { purchasePrice, arv } = acquisition;
    const { roi, riskScore, projectedSpread, arvRatio, capitalEfficiency, cashRequired } = outputs;
    const totalRehab = rehab.estimate * (1 + rehab.contingency / 100);

    // Purchase Price Optimization
    if (arvRatio > 0.70) {
      const targetPrice = arv * 0.65;
      const reduction = purchasePrice - targetPrice;
      if (reduction > 0) {
        const newRoi = cashRequired > 0 ? ((projectedSpread + reduction) / (cashRequired - reduction * (capitalStack.downPayment / 100))) * 100 : 0;
        suggestions.push({
          type: 'optimize',
          priority: 'high',
          title: 'Negotiate Purchase Price',
          description: `Reduce purchase to $${(targetPrice / 1000).toFixed(0)}K (-$${(reduction / 1000).toFixed(0)}K) to hit 65% ARV ratio`,
          impact: `ROI improves to ~${Math.min(newRoi, 150).toFixed(0)}%`,
          icon: 'TrendingUp'
        });
      }
    }

    // ROI Improvement
    if (roi < 25 && roi > 0) {
      const targetProfit = cashRequired * 0.30;
      const profitGap = targetProfit - projectedSpread;
      if (profitGap > 0) {
        suggestions.push({
          type: 'warning',
          priority: 'medium',
          title: 'Thin ROI Margin',
          description: `Current ROI is ${roi.toFixed(1)}%. Need $${(profitGap / 1000).toFixed(0)}K more profit to reach 30% target`,
          impact: 'Consider reducing offer or rehab scope',
          icon: 'AlertTriangle'
        });
      }
    }

    // Rehab Scope Analysis
    if (totalRehab / purchasePrice > 0.25) {
      const targetRehab = purchasePrice * 0.20;
      suggestions.push({
        type: 'caution',
        priority: 'medium',
        title: 'High Rehab Ratio',
        description: `Rehab is ${((totalRehab / purchasePrice) * 100).toFixed(0)}% of purchase. Consider scope at $${(targetRehab / 1000).toFixed(0)}K`,
        impact: 'Reduces execution risk',
        icon: 'Hammer'
      });
    }

    // ARV Validation
    if (arv > 0 && purchasePrice > 0) {
      const spread70 = arv * 0.70 - purchasePrice - totalRehab;
      if (spread70 < 20000) {
        suggestions.push({
          type: 'insight',
          priority: 'low',
          title: 'Validate ARV',
          description: `At 70% rule, spread is $${(spread70 / 1000).toFixed(0)}K. Confirm ARV with 3+ comps`,
          impact: 'Protects against market shifts',
          icon: 'Target'
        });
      }
    }

    // Positive Reinforcement
    if (roi >= 30 && riskScore < 40) {
      suggestions.push({
        type: 'success',
        priority: 'high',
        title: 'Strong Deal Profile',
        description: `${roi.toFixed(0)}% ROI with low risk score. This deal exceeds typical thresholds`,
        impact: 'Ready for execution',
        icon: 'CheckCircle'
      });
    }

    // Capital Efficiency
    if (capitalEfficiency < 0.15 && projectedSpread > 0) {
      suggestions.push({
        type: 'optimize',
        priority: 'medium',
        title: 'Improve Capital Efficiency',
        description: `Current ${(capitalEfficiency * 100).toFixed(0)}% return on capital. Consider higher leverage`,
        impact: `Increase down payment leverage to boost returns`,
        icon: 'Wallet'
      });
    }

    // Holding Period
    if (exitStrategy.holdingMonths > 8) {
      const monthlyCost = outputs.totalHoldingCosts / exitStrategy.holdingMonths;
      const savings = monthlyCost * 2;
      suggestions.push({
        type: 'insight',
        priority: 'low',
        title: 'Shorten Hold Period',
        description: `${exitStrategy.holdingMonths} months is extended. Target 6 months to save $${(savings / 1000).toFixed(0)}K`,
        impact: 'Reduces carrying costs',
        icon: 'Clock'
      });
    }

    return suggestions.slice(0, 4); // Limit to top 4 recommendations
  }, [acquisition, rehab, capitalStack, exitStrategy, outputs]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        {/* Header */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold tracking-tight">
                Capital <span className="text-[#D4AF37]">Model</span>
              </h1>
              <p className="text-[9px] text-white/30 uppercase tracking-[0.15em]">Model the financial structure of your deal before raising capital.</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                className="border-white/[0.1] text-white/50 hover:text-white"
                onClick={() => {
                  setAcquisition({ purchasePrice: 150000, arv: 250000 });
                  setRehab({ estimate: 35000, contingency: 10 });
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-medium">
                Save Model
              </Button>
            </div>
          </div>
        </header>

        {/* Model Outputs Strip */}
        <section className="border-b border-white/[0.06] bg-black/80 px-6 py-6">
          <div className="grid grid-cols-5 gap-6">
            {/* Projected Spread */}
            <OutputMetric
              label="Projected Spread"
              value={`$${(outputs.projectedSpread / 1000).toFixed(1)}K`}
              positive={outputs.projectedSpread > 0}
              large
            />
            
            {/* Risk Score */}
            <OutputMetric
              label="Risk Score"
              value={outputs.riskScore}
              status={outputs.riskScore < 40 ? 'good' : outputs.riskScore < 60 ? 'warning' : 'danger'}
              icon={AlertTriangle}
            />
            
            {/* Execution Fit */}
            <OutputMetric
              label="Execution Fit"
              value={outputs.executionFit}
              status={outputs.executionFit === 'High' ? 'good' : outputs.executionFit === 'Medium' ? 'warning' : 'danger'}
              icon={Target}
            />
            
            {/* Exit Confidence */}
            <OutputMetric
              label="Exit Confidence"
              value={`${outputs.exitConfidence}%`}
              status={outputs.exitConfidence > 75 ? 'good' : outputs.exitConfidence > 55 ? 'warning' : 'danger'}
              icon={Zap}
            />
            
            {/* Time to Close */}
            <OutputMetric
              label="Time to Close"
              value={outputs.timeToClose}
              icon={Clock}
            />
          </div>
        </section>

        {/* Main Content */}
        <div className="p-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left: Input Modules */}
            <div className="col-span-8 space-y-6">
              {/* Acquisition Module */}
              <InputModule 
                icon={Building2} 
                title="Acquisition"
                description="Property purchase parameters"
              >
                <div className="grid grid-cols-2 gap-6">
                  <InputField
                    label="Purchase Price"
                    value={acquisition.purchasePrice}
                    onChange={(v) => setAcquisition({ ...acquisition, purchasePrice: v })}
                    prefix="$"
                    type="currency"
                  />
                  <InputField
                    label="After Repair Value (ARV)"
                    value={acquisition.arv}
                    onChange={(v) => setAcquisition({ ...acquisition, arv: v })}
                    prefix="$"
                    type="currency"
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-white/[0.06]">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40">Purchase to ARV Ratio</span>
                    <span className={`font-mono ${(acquisition.purchasePrice / acquisition.arv) < 0.7 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}`}>
                      {((acquisition.purchasePrice / acquisition.arv) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </InputModule>

              {/* Rehab Module */}
              <InputModule 
                icon={Hammer} 
                title="Rehab Scope"
                description="Renovation budget and contingency"
              >
                <div className="grid grid-cols-2 gap-6">
                  <InputField
                    label="Rehab Estimate"
                    value={rehab.estimate}
                    onChange={(v) => setRehab({ ...rehab, estimate: v })}
                    prefix="$"
                    type="currency"
                  />
                  <InputField
                    label="Contingency"
                    value={rehab.contingency}
                    onChange={(v) => setRehab({ ...rehab, contingency: v })}
                    suffix="%"
                    type="percent"
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-white/[0.06]">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40">Total Rehab Budget</span>
                    <span className="font-mono font-bold text-white">
                      ${((rehab.estimate * (1 + rehab.contingency / 100)) / 1000).toFixed(1)}K
                    </span>
                  </div>
                </div>
              </InputModule>

              {/* Capital Stack Module */}
              <InputModule 
                icon={Wallet} 
                title="Capital Stack"
                description="Financing structure and costs"
              >
                <div className="grid grid-cols-2 gap-6">
                  <InputField
                    label="Down Payment"
                    value={capitalStack.downPayment}
                    onChange={(v) => setCapitalStack({ ...capitalStack, downPayment: v })}
                    suffix="%"
                    type="percent"
                  />
                  <InputField
                    label="Interest Rate"
                    value={capitalStack.interestRate}
                    onChange={(v) => setCapitalStack({ ...capitalStack, interestRate: v })}
                    suffix="%"
                    type="percent"
                  />
                  <InputField
                    label="Loan Term"
                    value={capitalStack.loanTermMonths}
                    onChange={(v) => setCapitalStack({ ...capitalStack, loanTermMonths: v })}
                    suffix="mo"
                    type="number"
                  />
                  <InputField
                    label="Closing Costs"
                    value={capitalStack.closingCosts}
                    onChange={(v) => setCapitalStack({ ...capitalStack, closingCosts: v })}
                    prefix="$"
                    type="currency"
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40">Loan Amount</span>
                    <span className="font-mono font-bold text-white">${(outputs.loanAmount / 1000).toFixed(1)}K</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40">Cash Required</span>
                    <span className="font-mono text-[#D4AF37]">${(outputs.cashRequired / 1000).toFixed(1)}K</span>
                  </div>
                </div>
              </InputModule>

              {/* Exit Strategy Module */}
              <InputModule 
                icon={LogOut} 
                title="Exit Strategy"
                description="Disposition and holding parameters"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Strategy Type</Label>
                    <Select 
                      value={exitStrategy.type} 
                      onValueChange={(v) => setExitStrategy({ ...exitStrategy, type: v })}
                    >
                      <SelectTrigger className="bg-white/[0.02] border-white/[0.06] text-white h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-white/[0.08]">
                        <SelectItem value="flip">Flip (Sell)</SelectItem>
                        <SelectItem value="brrrr">BRRRR (Refi)</SelectItem>
                        <SelectItem value="wholesale">Wholesale</SelectItem>
                        <SelectItem value="hold">Buy & Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <InputField
                    label="Holding Period"
                    value={exitStrategy.holdingMonths}
                    onChange={(v) => setExitStrategy({ ...exitStrategy, holdingMonths: v })}
                    suffix="mo"
                    type="number"
                  />
                  <InputField
                    label="Selling Costs"
                    value={exitStrategy.sellingCosts}
                    onChange={(v) => setExitStrategy({ ...exitStrategy, sellingCosts: v })}
                    suffix="%"
                    type="percent"
                  />
                  <InputField
                    label="Monthly Holding Costs"
                    value={exitStrategy.monthlyHoldingCosts}
                    onChange={(v) => setExitStrategy({ ...exitStrategy, monthlyHoldingCosts: v })}
                    prefix="$"
                    type="currency"
                  />
                </div>
              </InputModule>
            </div>

            {/* Right: AI Intelligence Panel */}
            <div className="col-span-4 space-y-6">
              {/* ROI Summary */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Return Analysis</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Total Investment</span>
                    <span className="text-lg font-mono font-bold text-white">${(outputs.totalInvestment / 1000).toFixed(1)}K</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Net Profit</span>
                    <span className={`text-lg font-mono ${outputs.projectedSpread > 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
                      ${(outputs.projectedSpread / 1000).toFixed(1)}K
                    </span>
                  </div>
                  <div className="pt-4 border-t border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/50">ROI</span>
                      <span className={`text-2xl font-light font-mono ${outputs.roi > 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
                        {outputs.roi.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-white/40">Annualized</span>
                      <span className="text-sm font-mono font-bold text-white/50">{outputs.annualizedRoi.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Sensitivity Analysis */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">AI Sensitivity</h3>
                  <Zap className="w-4 h-4 text-[#D4AF37]" />
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white/[0.02] rounded p-4">
                    <div className="text-[10px] text-white/40 uppercase tracking-wider mb-2">If ARV drops 5%</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/50">Spread Delta</span>
                      <span className={`font-mono ${outputs.spreadDelta < 0 ? 'text-[#FF3B30]' : 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]'}`}>
                        {outputs.spreadDelta > 0 ? '+' : ''}${(outputs.spreadDelta / 1000).toFixed(1)}K
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm text-white/50">New Profit</span>
                      <span className={`font-mono ${outputs.netProfit95 > 0 ? 'text-white/70' : 'text-[#FF3B30]'}`}>
                        ${(outputs.netProfit95 / 1000).toFixed(1)}K
                      </span>
                    </div>
                  </div>

                  <div className="bg-white/[0.02] rounded p-4">
                    <div className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Capital Efficiency</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/50">Multiple</span>
                      <span className={`text-lg font-mono ${outputs.capitalEfficiency > 0.2 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-white/70'}`}>
                        {outputs.capitalEfficiency.toFixed(2)}x
                      </span>
                    </div>
                    <p className="text-[10px] text-white/30 mt-2">
                      Every $1 invested returns ${(1 + outputs.capitalEfficiency).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Recommendations Panel */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">AI Recommendations</h3>
                  </div>
                  <span className="text-[9px] px-2 py-0.5 bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] rounded uppercase tracking-wider">Live</span>
                </div>
                
                {recommendations.length === 0 ? (
                  <div className="text-center py-6">
                    <Lightbulb className="w-8 h-8 text-white/30 mx-auto mb-2" />
                    <p className="text-xs text-white/40">Adjust inputs to see AI suggestions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recommendations.map((rec, idx) => (
                      <RecommendationCard key={idx} recommendation={rec} />
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">Actions</h3>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/deals/new')}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded hover:bg-[#D4AF37]/20 transition-all duration-300"
                  >
                    <span className="text-sm text-[#D4AF37]">Create Transaction</span>
                    <ArrowRight className="w-4 h-4 text-[#D4AF37]" />
                  </button>
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300">
                    <span className="text-sm text-white/50">Run Strategy Simulation</span>
                    <ArrowRight className="w-4 h-4 text-white/40" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Components

function OutputMetric({ label, value, positive, status, icon: Icon, large }) {
  const getStatusColor = () => {
    if (positive !== undefined) return positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]';
    if (status === 'good') return 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]';
    if (status === 'warning') return 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]';
    if (status === 'danger') return 'text-[#FF3B30]';
    return 'text-white';
  };

  return (
    <div>
      <div className="text-[9px] text-white/40 uppercase tracking-wider mb-2">{label}</div>
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`w-4 h-4 ${getStatusColor()}`} />}
        <span className={`${large ? 'text-2xl' : 'text-xl'} font-light font-mono tracking-tight ${getStatusColor()}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

function InputModule({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-3">
        <div className="w-8 h-8 bg-white/[0.02] rounded flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-white">{title}</h3>
          <p className="text-[10px] text-white/30">{description}</p>
        </div>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, prefix, suffix, type = 'number' }) {
  const formatValue = (val) => {
    if (type === 'currency') return val.toLocaleString();
    return val;
  };

  const parseValue = (str) => {
    const num = parseFloat(str.replace(/,/g, ''));
    return isNaN(num) ? 0 : num;
  };

  return (
    <div>
      <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">{label}</Label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">{prefix}</span>
        )}
        <Input
          type="text"
          value={formatValue(value)}
          onChange={(e) => onChange(parseValue(e.target.value))}
          className={`bg-white/[0.02] border-white/[0.06] text-white h-11 font-mono ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-10' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function RecommendationCard({ recommendation }) {
  const { type, priority, title, description, impact, icon } = recommendation;
  
  const getIcon = () => {
    switch (icon) {
      case 'TrendingUp': return TrendingUp;
      case 'AlertTriangle': return AlertTriangle;
      case 'Hammer': return Hammer;
      case 'Target': return Target;
      case 'CheckCircle': return CheckCircle;
      case 'Wallet': return Wallet;
      case 'Clock': return Clock;
      default: return Lightbulb;
    }
  };
  
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-emerald-500/[0.08]',
          border: 'border-emerald-500/20',
          iconColor: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]',
          titleColor: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]'
        };
      case 'optimize':
        return {
          bg: 'bg-[#D4AF37]/[0.08]',
          border: 'border-[#D4AF37]/20',
          iconColor: 'text-[#D4AF37]',
          titleColor: 'text-[#D4AF37]'
        };
      case 'warning':
        return {
          bg: 'bg-amber-500/[0.08]',
          border: 'border-amber-500/20',
          iconColor: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]',
          titleColor: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'
        };
      case 'caution':
        return {
          bg: 'bg-orange-500/[0.08]',
          border: 'border-orange-500/20',
          iconColor: 'text-orange-400',
          titleColor: 'text-orange-400'
        };
      default:
        return {
          bg: 'bg-white/[0.02]',
          border: 'border-white/[0.06]',
          iconColor: 'text-white/50',
          titleColor: 'text-white/70'
        };
    }
  };
  
  const Icon = getIcon();
  const styles = getTypeStyles();
  
  return (
    <div className={`${styles.bg} border ${styles.border} rounded p-3 transition-all hover:scale-[1.01]`}>
      <div className="flex items-start gap-3">
        <div className={`w-7 h-7 rounded flex items-center justify-center ${styles.bg} flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${styles.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm font-medium ${styles.titleColor}`}>{title}</h4>
            {priority === 'high' && (
              <span className="text-[8px] px-1.5 py-0.5 bg-red-500/20 text-[#FF3B30] rounded uppercase">High</span>
            )}
          </div>
          <p className="text-[11px] text-white/50 leading-relaxed">{description}</p>
          {impact && (
            <p className="text-[10px] text-white/40 mt-1.5 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" />
              {impact}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
